import { F } from '@/design/fonts';
import { T } from '@/design/tokens';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

type Level = 1 | 2 | 3 | 4 | 5 | 6;
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'children'> {
  children: ReactNode;
  level: Level;
  size?: Size;
}

const SIZE_PX: Record<Size, number> = {
  xs: 13,
  sm: 15,
  md: 18,
  lg: 22,
  xl: 28,
  xxl: 36,
};

const LEVEL_DEFAULT_SIZE: Record<Level, Size> = {
  1: 'xxl',
  2: 'xl',
  3: 'lg',
  4: 'md',
  5: 'sm',
  6: 'xs',
};

export function Heading({ children, level, size, style, ...rest }: HeadingProps) {
  const Tag = `h${level}` as const;
  const resolvedSize: Size = size ?? LEVEL_DEFAULT_SIZE[level];
  const useDisplayFont = level === 1 || level === 2;

  const baseStyle: CSSProperties = {
    margin: 0,
    fontFamily: useDisplayFont ? F.display : F.body,
    fontWeight: useDisplayFont ? 400 : 600,
    fontSize: SIZE_PX[resolvedSize],
    letterSpacing: useDisplayFont ? '0.01em' : '0.02em',
    color: T.text,
    lineHeight: 1.2,
  };

  return (
    <Tag {...rest} style={{ ...baseStyle, ...style }}>
      {children}
    </Tag>
  );
}
